import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import MailingObject, { getDescription, getMailingTypeInfo, getMailingTypesByModule } from 'src/app/models/MailingObject';
import { Module } from 'src/app/models/Module';
import { FetchMailingObjects, FetchModules, RemoveMailFromModule } from 'src/app/modules/module.actions';
import { ModuleMaintenanceService } from 'src/app/modules/module.maintenance.service';
import { ModuleState } from 'src/app/modules/module.state';
import { ConfirmationModalComponent } from 'src/app/shared/confirmation-modal/confirmation-modal.component';
import { UserState } from 'src/app/users/user.state';

import { AddMailToMailinglistModalComponent } from './add-mail-to-mailinglist-modal/add-mail-to-mailinglist-modal.component';

@Component({
    selector: 'app-mail-setup',
    templateUrl: './mail-setup.component.html',
    styleUrls: ['./mail-setup.component.scss'],
})
export class MailSetupComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    @Select(ModuleState.Modules) modules$: Observable<Module[]>;
    @Select(ModuleState.MailingObjectsPerModule)
    mailingObjectsPerModule$: Observable<{ module: string; mailingObjects: MailingObject[] }[]>;
    @Select(UserState.hasUserOwnerRole) isOwner$: Observable<boolean>;

    displayedModules: Module[];
    sessionModules: string[];
    isLoading = true;
    mailingObjectsPerModule: {
        module: string;
        mailingObjects: MailingObject[];
    }[];

    displayedColumns: string[] = ['mail', 'action', 'target', 'timing', 'edit'];
    dataSource: MatTableDataSource<MailingObject> = new MatTableDataSource<MailingObject>();
    @ViewChild(MatSort) sort: MatSort;

    constructor(
        private modalService: NgbModal,
        private store: Store,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private moduleMaintenanceService: ModuleMaintenanceService,
    ) {}

    ngOnInit(): void {
        this.sessionModules = JSON.parse(sessionStorage.getItem('allowedModules'));
        this.subscriptions.add(
            this.modules$.subscribe((modules) => {
                if (modules == null) {
                    this.store.dispatch(new FetchModules());
                } else {
                    this.store.dispatch(new FetchMailingObjects());
                    this.displayedModules = modules.filter((m) => this.sessionModules.find((k) => k == m.key));
                }
            }),
        );

        this.mailingObjectsPerModule$.subscribe((objects) => {
            if (objects != null && objects.every((o) => o.mailingObjects != undefined)) {
                this.mailingObjectsPerModule = objects;
                this.isLoading = false;
            } else {
                this.store.dispatch(new FetchMailingObjects());
            }
        });
    }

    getMailObjectsByModuleKey(moduleKey: string): MailingObject[] {
        const mailingObjects = this.mailingObjectsPerModule.find((mo) => mo.module == moduleKey);
        if (mailingObjects) {
            return mailingObjects.mailingObjects;
        }
        return [];
    }

    openAddMailModal(module: Module) {
        this.logger.debug('Opening AddMailToMailinglistModal');
        const modalRef = this.modalService.open(AddMailToMailinglistModalComponent, { windowClass: 'modal-prompt', animation: false });
        modalRef.componentInstance.module = module;
    }

    deleteMail(module: Module, mail: MailingObject) {
        this.logger.debug('Opening confirmationModal');
        const modalRef = this.modalService.open(ConfirmationModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.type = 'Delete';
        modalRef.componentInstance.title = 'Delete email';
        const text = module != null ? module.title : 'global';
        modalRef.componentInstance.message =
            'Are you sure you want to remove this email from the ' + text + ' mailing list?\nThis email will no longer receive notifications.';
        this.subscriptions.add(
            modalRef.componentInstance.closeEvent.subscribe((val) => {
                if (val) {
                    this.logger.debug('Attempt to remove mail');
                    this.store.dispatch(new RemoveMailFromModule(module.uuid, mail.uuid)).subscribe(
                        (res) => {
                            this.toastr.info('Successfully removed mail.');
                        },
                        (err) => {
                            this.logger.error('Failed to remove mail from module');
                            this.toastr.error('Oops, something went wrong. Please try again later.');
                        },
                    );
                }
            }),
        );
    }

    getTypeDescription(mailingTypeString) {
        return getMailingTypeInfo(mailingTypeString)[1];
    }

    getCronDescription(cron) {
        const description = getDescription(cron);
        if (description) {
            return description;
        }
        return 'none';
    }

    getMailingTypesByModule(name) {
        return getMailingTypesByModule(name);
    }

    supportsTestMail(_mailingType: string): boolean {
        // Every rule can be triggered manually now: the backend either runs a
        // dedicated single-recipient test path (PROJECT_BUDGET_...) or fires
        // the actual nightly Quartz job to all configured recipients.
        return true;
    }

    sendTestMail(mail: MailingObject) {
        if (!mail.emails || mail.emails.length === 0) {
            this.toastr.warning('No email addresses configured for this mail rule.');
            return;
        }

        const email = mail.emails[0];
        this.logger.debug('Sending test mail for', mail.mailingType, 'to', email);
        this.toastr.info('Triggering ' + mail.mailingType + '...');

        this.subscriptions.add(
            this.moduleMaintenanceService.sendTestMail(mail.mailingType, email).subscribe(
                (res) => {
                    this.toastr.success(res?.message || 'Test mail triggered.');
                },
                (err) => {
                    this.logger.error('Failed to send test mail', err);
                    const msg = err?.error?.message || err?.message || 'Failed to send test mail. Please try again later.';
                    this.toastr.error(msg);
                },
            ),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
